import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAuthenticationDataContext } from '../../../Helpers/AuthenticationDataProvider';
import useFetchWrapper, {
	HttpResponse,
} from '../../../Helpers/useFetchWrapper';
import useScoreHelper, { Score } from '../../../Helpers/useScoreHelper';

import { Action } from '../../../components/Popup/FormPopup.model';
import {
	Conference,
	CurrentUserBodyRequest,
	Education,
	TabStatus,
	WorkExperience,
} from './EditProfile.model';
import { CurrentUserResponse } from '../EditCurrentProfile.model';
import { useHistory } from 'react-router-dom';
import {useAuthorizationDataContext} from "../../../Helpers/AuthorizationDataProvider";

export const useCurrentProfile = () => {
	const [averageScore, setAverageScore] = useState<Score | undefined>();
	const [conferences, setConferences] = useState<Conference[]>();
	const [currentUser, setCurrentUser] = useState<CurrentUserResponse>();
	const [workExp, setWorkExp] = useState<WorkExperience[]>();
	const [education, setEducation] = useState<Education[]>();
	const [showExperienceForm, setShowExperienceForm] = useState(false);
	const [showEducationForm, setShowEducationForm] = useState(false);
	const [showImagePopup, setShowImagePopup] = useState<boolean>(false);
	const [tabStatus, setTabStatus] = useState<TabStatus>(TabStatus.Loading);

	const { get, put, post, remove } = useFetchWrapper();
	const intl = useIntl();
	const { userId, setFullName } = useAuthenticationDataContext();
	const { getScoreByAverageRating } = useScoreHelper();
	const history = useHistory();
	const { role } = useAuthorizationDataContext()

	const getFormPopupActions = (onCancel: () => void): Action[] => {
		return [
			{ onClick: onCancel, title: 'cancel', type: 'default' },
			{ isSubmitButton: true, title: 'save', type: 'primary' },
		];
	};

	useEffect(() => {
		getCurrentUser();
		if(role === "ApplicantMentee") {
			getFutureConferences()
		} else {
			getConferences();
		}
		getWorkExperience();
		getEducation();
		
		setTabStatus(TabStatus.Loaded);
	}, []);

	useEffect(() => {
		currentUser?.role === 'Mentor' && getUserRating();
	}, [currentUser]);

	const getConferences = async () => {
		const response: HttpResponse<{ conferences: Conference[], count: number }> = await get('/conference/all?page=1&size=1000');
		response.parsedBody && setConferences(response.parsedBody.conferences);
	};
	
	const getFutureConferences = async () => {
		const response: HttpResponse<{ conferences: Conference[], count: number }> = await get('/conference/all?isFuture=true&page=1&size=1000');
		response.parsedBody && setConferences(response.parsedBody.conferences);
	};

	const getCurrentUser = async () => {
		const response: HttpResponse<CurrentUserResponse> = await get(
			`/user/profile/${userId}`
		);
		if (response.parsedBody) {
			const user = response.parsedBody;
			setCurrentUser(user);
		}
	};

	const updateProfile = async (data: any) => {
		const {
		
			firstName,
		
			lastName,
			
			 } = data;
			 console.log(data)
		const body: CurrentUserBodyRequest = {
			...data
		};
		await put(`/user/profile/${userId}`, body);
		setFullName(firstName, lastName);
		getCurrentUser();
		navigateTo('/my-profile');
	};

	const formatDate = (date: string) => {
		return intl.formatDate(new Date(date), { month: 'long', year: 'numeric' });
	};

	const formatData = (formData: any) => {
		if (formData.startDate)
			formData.startDate = formData.startDate.format('YYYY-MM-DD');
		if (formData.endDate)
			formData.endDate = formData.endDate.format('YYYY-MM-DD');
		if (formData.publishingYear)
			formData.publishingYear = formData.publishingYear.format('YYYY');
		Object.keys(formData).forEach((key) => {
			if (formData[key] === '') formData[key] = undefined;
		});
		return formData;
	};

	const getUserRating = async () => {
		const response: HttpResponse<any> = await get(
			`/review/rating/${userId}?page=1&size=5`
		);

		if (response.parsedBody) {
			const { averageScore } = response.parsedBody;

			setAverageScore(getScoreByAverageRating(averageScore));
		}
	};

	const getWorkExperience = async () => {
		const response: HttpResponse<WorkExperience[]> = await get(
			`/user/profile/work-experience/${userId}`
		);

		const workExperience: WorkExperience[] =
			response.parsedBody?.map((exp: WorkExperience) => ({
				currentPosition: exp.currentPosition,
				description: exp.description,
				endDate: exp.endDate,
				institution: exp.institution,
				location: exp.location,
				startDate: exp.startDate,
				workExperienceId: exp.workExperienceId,
			})) || [];

		setWorkExp(
			workExperience.sort((a, b) => (a.startDate < b.startDate ? 1 : -1))
		);
	};

	const submitWorkExperience = async (formData: WorkExperience) => {
		formData = formatData(formData);
		await post('/user/profile/work-experience', formData);
		getWorkExperience();
	};

	const editWorkExperience = async (
		formData: WorkExperience,
		id: string | undefined
	) => {
		formData = formatData(formData);
		await put(`/user/profile/work-experience/${id}`, formData);
		getWorkExperience();
	};

	const deleteWorkExperience = async (workExperienceId: string | undefined) => {
		await remove(`/user/profile/work-experience/${workExperienceId}`);
		getWorkExperience();
	};

	const getEducation = async () => {
		const response: HttpResponse<Education[]> = await get(
			`/user/profile/education/${userId}`
		);

		const education: Education[] =
			response.parsedBody?.map((ed: Education) => ({
				description: ed.description,
				educationId: ed.educationId,
				endDate: ed.endDate,
				institution: ed.institution,
				location: ed.location,
				obtainedDegree: ed.obtainedDegree,
				startDate: ed.startDate,
			})) || [];

		setEducation(
			education.sort((a, b) => (a.startDate < b.startDate ? 1 : -1))
		);
	};

	const submitEducation = async (formData: Education) => {
		formData = formatData(formData);
		await post('/user/profile/education', formData);
		getEducation();
	};

	const editEducation = async (formData: Education, id: string | undefined) => {
		formData = formatData(formData);
		await put(`/user/profile/education/${id}`, formData);
		getEducation();
	};

	const deleteEducation = async (educationId: string | undefined) => {
		await remove(`/user/profile/education/${educationId}`);
		getEducation();
	};

	const toggle = (
		set: React.Dispatch<React.SetStateAction<boolean>>,
		value: boolean
	) => {
		set(!value);
	};

	const navigateTo = (path: string, state?: any, search?: string) => {
		history.push({ pathname: path, search, state });
	};

	return {
		deleteEducation,
		deleteWorkExperience,
		editEducation,
		editWorkExperience,
		formatDate,
		getFormPopupActions,
		navigateTo,
		setCurrentUser,
		state: {
			averageScore,
			conferences,
			currentUser,
			education,
			showEducationForm,
			showExperienceForm,
			showImagePopup,
			tabStatus,
			workExp,
		},
		submitEducation,
		submitWorkExperience,
		toggleEducationForm: () => toggle(setShowEducationForm, showEducationForm),
		toggleExperienceForm: () =>
			toggle(setShowExperienceForm, showExperienceForm),
		toggleImagePopup: () => toggle(setShowImagePopup, showImagePopup),
		updateProfile,
	};
};
export default useCurrentProfile;